// Sonner behavior port; see components/scenario-toaster/THIRD_PARTY_NOTICES.md.
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ScenarioToast, ScenarioToastOptions, ScenarioToastState, ScenarioToastType } from '../models/scenario-toast.model';

interface ToastTimer {
  remaining: number;
  started: number;
  handle?: ReturnType<typeof setTimeout>;
  exit?: ReturnType<typeof setTimeout>;
}

@Injectable()
export class ScenarioToastService implements OnDestroy {
  enabled = true;
  private readonly state = new BehaviorSubject<ScenarioToastState>({ toasts: [], held: false });
  readonly state$ = this.state.asObservable();
  private toasts: ScenarioToast[] = [];
  private readonly timers = new Map<string, ToastTimer>();
  private readonly pauses = new Set<string>();
  private readonly queued = new Set<string>();
  private held = false;
  private destroyed = false;
  private sequence = 0;

  constructor(private readonly zone: NgZone) { }

  show(title: string, options: ScenarioToastOptions = {}): string { return this.create('default', title, options); }
  success(title: string, options: ScenarioToastOptions = {}): string { return this.create('success', title, options); }
  info(title: string, options: ScenarioToastOptions = {}): string { return this.create('info', title, options); }
  warning(title: string, options: ScenarioToastOptions = {}): string { return this.create('warning', title, options); }
  error(title: string, options: ScenarioToastOptions = {}): string { return this.create('error', title, options); }
  loading(title: string, options: ScenarioToastOptions = {}): string { return this.create('loading', title, options); }

  update(id: string, patch: Partial<ScenarioToastOptions> & { type?: ScenarioToastType }): void {
    const toast = this.toasts.find(item => item.id === id && !item.removed);
    if (!toast || this.destroyed) { return; }
    const type = patch.type || toast.type;
    const next = { ...toast, ...patch, type, id, settled: toast.settled || (toast.type === 'loading' && type !== 'loading') };
    if (!next.announce) { next.action = undefined; next.cancel = undefined; }
    this.toasts = this.toasts.map(item => item === toast ? next : item);
    const timer = this.timers.get(id);
    if (timer) {
      this.stop(timer);
      timer.remaining = type === 'loading' ? Infinity : this.lifetime(next.duration);
    }
    this.publish();
    this.syncTimers();
  }

  markMounted(id: string): void {
    if (this.destroyed || this.queued.has(id)) { return; }
    const toast = this.toasts.find(item => item.id === id && !item.removed);
    if (!toast || toast.mounted) { return; }
    this.toasts = this.toasts.map(item => item === toast ? { ...item, mounted: true } : item);
    this.publish();
    this.syncTimers();
  }

  dismiss(id?: string): void {
    if (this.destroyed) { return; }
    this.toasts.filter(item => !item.removed && (id === undefined || item.id === id)).forEach(toast => {
      const timer = this.timers.get(toast.id);
      if (timer) { this.stop(timer); }
      if (this.queued.delete(toast.id)) {
        this.toasts = this.toasts.filter(item => item.id !== toast.id);
        this.timers.delete(toast.id);
        return;
      }
      this.toasts = this.toasts.map(item => item.id === toast.id ? { ...item, removed: true } : item);
      if (timer) {
        this.zone.runOutsideAngular(() => {
          timer.exit = setTimeout(() => this.zone.run(() => {
            this.toasts = this.toasts.filter(item => item.id !== toast.id);
            this.timers.delete(toast.id);
            this.publish();
          }), 200);
        });
      }
    });
    this.publish();
  }

  setHeld(held: boolean): void {
    if (this.destroyed || held === this.held) { return; }
    this.held = held;
    if (!held) { this.queued.clear(); }
    this.syncTimers();
    this.publish();
  }

  setPaused(reason: string, paused: boolean): void {
    if (this.destroyed) { return; }
    if (paused) { this.pauses.add(reason); } else { this.pauses.delete(reason); }
    this.syncTimers();
  }

  ngOnDestroy(): void {
    if (this.destroyed) { return; }
    this.destroyed = true;
    this.timers.forEach(timer => { this.stop(timer); clearTimeout(timer.exit); });
    this.timers.clear();
    this.queued.clear();
    this.pauses.clear();
    this.toasts = [];
    this.state.next({ toasts: [], held: false });
    this.state.complete();
  }

  private create(type: ScenarioToastType, title: string, options: ScenarioToastOptions): string {
    const id = options.id || `scenario-toast-${++this.sequence}`;
    if (!this.enabled || this.destroyed) { return id; }
    const existing = this.toasts.find(item => item.id === id && !item.removed);
    if (existing) { this.update(id, { ...options, type, title }); return id; }
    // A dismissed id must not share timers or a DOM identity with its exiting row.
    const nextId = this.toasts.some(item => item.id === id) ? `scenario-toast-${++this.sequence}` : id;
    const toast: ScenarioToast = { ...options, id: nextId, title, type, announce: options.announce !== false,
      mounted: false, removed: false, settled: false };
    if (!toast.announce) { toast.action = undefined; toast.cancel = undefined; }
    this.toasts = [toast, ...this.toasts];
    this.timers.set(nextId, { remaining: type === 'loading' ? Infinity : this.lifetime(options.duration), started: 0 });
    if (this.held) { this.queued.add(nextId); }
    this.publish();
    return nextId;
  }

  private lifetime(duration?: number): number {
    return duration === Infinity ? Infinity : duration !== undefined && Number.isFinite(duration) ? Math.max(0, duration) : 4000;
  }

  private publish(): void {
    if (!this.destroyed) { this.state.next({ toasts: this.toasts.filter(toast => !this.queued.has(toast.id)), held: this.held }); }
  }

  private stop(timer: ToastTimer): void {
    if (timer.handle !== undefined) {
      clearTimeout(timer.handle);
      timer.handle = undefined;
      timer.remaining = Math.max(0, timer.remaining - (Date.now() - timer.started));
    }
  }

  private syncTimers(): void {
    this.toasts.forEach(toast => {
      const timer = this.timers.get(toast.id);
      if (!timer) { return; }
      if (this.held || this.pauses.size || !toast.mounted || toast.removed || toast.type === 'loading') { this.stop(timer); return; }
      if (timer.handle !== undefined || timer.remaining === Infinity) { return; }
      this.zone.runOutsideAngular(() => {
        timer.started = Date.now();
        timer.handle = setTimeout(() => this.zone.run(() => {
          timer.handle = undefined;
          this.dismiss(toast.id);
        }), timer.remaining);
      });
    });
  }
}
